import { useState, FormEvent } from 'react';
import { Button, Input } from '../ui';
import { useAuth } from '../../stores/auth.store';

interface ResetPasswordFormProps {
  email: string;
  onSuccess?: () => void;
  onBack?: () => void;
}

export function ResetPasswordForm({
  email,
  onSuccess,
  onBack,
}: ResetPasswordFormProps) {
  const { confirmResetPassword, isLoading, error, clearError } = useAuth();
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const validateForm = (): boolean => {
    if (newPassword.length < 8) {
      setValidationError('Password must be at least 8 characters');
      return false;
    }
    if (newPassword !== confirmPassword) {
      setValidationError('Passwords do not match');
      return false;
    }
    setValidationError(null);
    return true;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    clearError();

    if (!validateForm()) return;

    const success = await confirmResetPassword(email, code, newPassword);
    if (success) {
      onSuccess?.();
    }
  };

  const displayError = validationError || error;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="text-center mb-6">
        <p className="text-slate-300">
          Enter the code sent to{' '}
          <span className="text-white font-medium">{email}</span>
        </p>
      </div>

      <Input
        label="Verification Code"
        type="text"
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
        placeholder="000000"
        required
        fullWidth
        disabled={isLoading}
        className="text-center text-2xl tracking-widest font-mono"
      />

      <Input
        label="New Password"
        type="password"
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        placeholder="••••••••"
        required
        fullWidth
        disabled={isLoading}
        helperText="At least 8 characters"
      />

      <Input
        label="Confirm New Password"
        type="password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        placeholder="••••••••"
        required
        fullWidth
        disabled={isLoading}
        state={
          confirmPassword && newPassword !== confirmPassword
            ? 'error'
            : 'default'
        }
      />

      {displayError && (
        <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
          <p className="text-sm text-red-400">{displayError}</p>
        </div>
      )}

      <Button
        type="submit"
        fullWidth
        isLoading={isLoading}
        disabled={code.length !== 6}
      >
        Reset Password
      </Button>

      <button
        type="button"
        onClick={onBack}
        className="w-full text-center text-sm text-slate-400 hover:text-white transition-colors"
      >
        ← Back
      </button>

      {import.meta.env.DEV && (
        <div className="mt-4 p-3 rounded-lg bg-slate-700/50 text-xs text-slate-400">
          <p className="font-medium text-slate-300 mb-1">Development Mode:</p>
          <p>Any 6-digit code (e.g., 123456) will be accepted.</p>
        </div>
      )}
    </form>
  );
}
